// PR-J — TPS Oversight staff console. The cross-TPS command center: the
// servicer directory + certification/onboarding status, the approval and
// correction monitoring queues, fee configuration (per-TPS modal), the money
// pass-through summary, and the break-glass per-loan emergency switch. All
// reads are gated server-side on Portfolio.OversightView; this page-level
// guard is defense-in-depth and matches the route's RequireAuth.

import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  OverflowMenu,
  PageHeader,
  Skeleton,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "@/design-system/components/ui";
import { formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { formatCalendarDate, useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import {
  useAcknowledgeAnomaly,
  useTpsAnomalies,
  useTpsApprovalQueue,
  useTpsCorrectionQueue,
  useTpsMoneyOversight,
  useTpsOversightDashboard,
} from "@/hooks/portfolioAdmin/useTpsOversight";
import { TpsFeeScheduleModal } from "@/components/portfolioAdmin/tpsOversight/TpsFeeScheduleModal";
import { TpsLoansDrawer } from "@/components/portfolioAdmin/tpsOversight/TpsLoansDrawer";
import type { TpsOversightRowDto } from "@/api/portfolioAdmin/tpsOversightApi";

const VIEW_PERMISSION = "Portfolio.OversightView";

export default function TpsOversightPage() {
  const { hasPermission } = useAuth();

  const dashboard = useTpsOversightDashboard();
  const approvals = useTpsApprovalQueue();
  const corrections = useTpsCorrectionQueue();
  const money = useTpsMoneyOversight();
  const anomalies = useTpsAnomalies();

  // Which TPS row (if any) has its fee modal / loans drawer open.
  const [feeRow, setFeeRow] = useState<TpsOversightRowDto | null>(null);
  const [loansRow, setLoansRow] = useState<TpsOversightRowDto | null>(null);

  if (!hasPermission(VIEW_PERMISSION)) {
    return (
      <div className="p-space-5">
        <PageHeader eyebrow="Finance" title="TPS Oversight" />
        <EmptyState
          title="Not authorized"
          description="You don't have permission to view the TPS Oversight console."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-5 p-space-5">
      <PageHeader
        eyebrow="Finance"
        title="TPS Oversight"
        description="Cross-TPS command center: the servicer directory and certification status, the bill-approval and correction queues, per-servicer fee configuration, the money pass-through summary, and the break-glass emergency switch for a loan whose servicer is in distress."
      />

      <SummarySection query={dashboard} />

      <GuardrailsSection query={anomalies} />

      <DirectorySection
        query={dashboard}
        onOpenFees={setFeeRow}
        onOpenLoans={setLoansRow}
      />

      <ApprovalSection query={approvals} />

      <CorrectionSection query={corrections} />

      <MoneySection query={money} />

      {feeRow && (
        <TpsFeeScheduleModal
          tpsId={feeRow.tpsId}
          tpsName={feeRow.name}
          currentRecoveryRate={feeRow.collectionsRecoveryFeeRate}
          open
          onClose={() => setFeeRow(null)}
        />
      )}
      {loansRow && (
        <TpsLoansDrawer
          tpsId={loansRow.tpsId}
          tpsName={loansRow.name}
          open
          onClose={() => setLoansRow(null)}
        />
      )}
    </div>
  );
}

// ——— Summary KPIs ———

function SummarySection({
  query,
}: {
  query: ReturnType<typeof useTpsOversightDashboard>;
}) {
  if (query.isLoading) return <Skeleton className="h-24 w-full" />;
  if (query.isError || !query.data) return null;
  const s = query.data.summary;
  return (
    <section
      className="grid grid-cols-2 gap-space-3 md:grid-cols-4 lg:grid-cols-7"
      data-testid="tps-oversight-summary"
    >
      <Kpi label="TPS providers" value={s.tpsCount} />
      <Kpi label="Certified" value={s.certifiedCount} />
      <Kpi label="Pending bills" value={s.pendingBillApprovals} />
      <Kpi label="Pending dists" value={s.pendingDistributions} />
      <Kpi label="Returned bills" value={s.returnedBills} />
      <Kpi label="Attention dists" value={s.attentionDistributions} />
      <Kpi
        label="Cleared, undistributed"
        value={formatCurrency(s.clearedUndistributed)}
      />
    </section>
  );
}

function Kpi({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <Card className="flex flex-col gap-space-1">
      <span className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
        {label}
      </span>
      <span className="font-display text-h3 font-bold tabular-nums text-onyx">
        {value}
      </span>
    </Card>
  );
}

// ——— Guardrails / fund-safety anomalies (Section 3) ———

const ANOMALY_KIND_LABEL: Record<string, string> = {
  UnexpectedPayout: "Unexpected payout",
  BankAccountChanged: "Bank account changed",
  BalanceShortfall: "Balance shortfall",
};

function GuardrailsSection({
  query,
}: {
  query: ReturnType<typeof useTpsAnomalies>;
}) {
  const { tz } = useDateFormatter();
  const acknowledge = useAcknowledgeAnomaly();

  return (
    <section>
      <SectionTitle>Fund-movement guardrails</SectionTitle>
      {query.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : query.isError ? (
        <p className="text-small text-status-error" role="alert">
          Couldn't load the clearing-account anomalies. Try again.
        </p>
      ) : !query.data || query.data.length === 0 ? (
        <EmptyState
          title="No fund-safety anomalies"
          description="No unexpected payouts, bank changes, or balance shortfalls have been detected on any clearing account."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>TPS</TH>
              <TH>Anomaly</TH>
              <TH className="text-right">Amount</TH>
              <TH>Detected</TH>
              <TH>Status</TH>
              <TH className="text-right">Action</TH>
            </TR>
          </THead>
          <TBody>
            {query.data.map((a) => (
              <TR key={a.id} data-testid={`tps-anomaly-${a.id}`}>
                <TD className="font-semibold text-onyx">{a.tpsName ?? "—"}</TD>
                <TD>
                  <div className="flex flex-col gap-space-1">
                    <Badge status={a.acknowledged ? "draft" : "error"}>
                      {ANOMALY_KIND_LABEL[a.kind] ?? a.kind}
                    </Badge>
                    <span className="text-caption text-gray-mid">
                      {a.message}
                    </span>
                  </div>
                </TD>
                <TD className="text-right tabular-nums">
                  {a.amount === null ? "—" : formatCurrency(a.amount)}
                </TD>
                <TD className="text-gray-mid">
                  {formatDateInTz(a.detectedAt, tz)}
                </TD>
                <TD>
                  <Badge status={a.acknowledged ? "finalized" : "review"}>
                    {a.acknowledged ? "Acknowledged" : "Open"}
                  </Badge>
                </TD>
                <TD className="text-right">
                  {a.acknowledged ? (
                    <span className="text-caption text-gray-mid">—</span>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() => acknowledge.mutate(a.id)}
                      disabled={acknowledge.isPending}
                    >
                      Acknowledge
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}

// ——— Directory ———

function DirectorySection({
  query,
  onOpenFees,
  onOpenLoans,
}: {
  query: ReturnType<typeof useTpsOversightDashboard>;
  onOpenFees: (row: TpsOversightRowDto) => void;
  onOpenLoans: (row: TpsOversightRowDto) => void;
}) {
  return (
    <section>
      <SectionTitle>TPS directory</SectionTitle>
      {query.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : query.isError ? (
        <p className="text-small text-status-error" role="alert">
          Couldn't load the TPS directory. Try again.
        </p>
      ) : !query.data || query.data.tps.length === 0 ? (
        <EmptyState
          title="No TPS providers"
          description="No Third-Party Servicers are configured yet."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Name</TH>
              <TH className="text-right">Loans</TH>
              <TH className="text-right">Pending bills</TH>
              <TH className="text-right">Pending dists</TH>
              <TH className="text-right">Returned</TH>
              <TH className="text-right">Attention</TH>
              <TH className="text-right">Cleared, undistributed</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {query.data.tps.map((row) => (
              <TR key={row.tpsId} data-testid={`tps-directory-row-${row.tpsId}`}>
                <TD>
                  <div className="flex flex-col gap-space-1">
                    <Link
                      to={`/portfolio-admin/tps/${row.tpsId}`}
                      className="font-semibold text-onyx underline-offset-2 hover:underline"
                    >
                      {row.name}
                    </Link>
                    <div className="flex flex-wrap items-center gap-space-1">
                      {row.isDefault && (
                        <Badge status="info">Default</Badge>
                      )}
                      <Badge status={row.isCertified ? "finalized" : "draft"}>
                        {row.isCertified ? "Certified" : "Not certified"}
                      </Badge>
                      <Badge
                        status={
                          row.stripeOnboardingComplete ? "finalized" : "review"
                        }
                      >
                        {row.stripeOnboardingComplete
                          ? "Onboarded"
                          : "Onboarding"}
                      </Badge>
                      {row.clearingPayoutInterval && (
                        <Badge
                          status={
                            row.clearingPayoutControlled
                              ? "finalized"
                              : "review"
                          }
                        >
                          {row.clearingPayoutControlled
                            ? "Manual payouts"
                            : "Auto payouts ⚠"}
                        </Badge>
                      )}
                    </div>
                    <span className="text-caption text-gray-mid">
                      Clearing: {row.clearingAccountStatus} · Commissions:{" "}
                      {row.commissionsAccountStatus}
                    </span>
                  </div>
                </TD>
                <TD className="text-right tabular-nums">
                  {row.assignedLoanCount}
                </TD>
                <TD className="text-right tabular-nums">
                  {row.pendingBillCount}
                </TD>
                <TD className="text-right tabular-nums">
                  {row.pendingDistributionCount}
                </TD>
                <TD className="text-right tabular-nums">
                  {row.returnedBillCount}
                </TD>
                <TD className="text-right tabular-nums">
                  {row.attentionDistributionCount}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(row.clearedUndistributed)}
                </TD>
                <TD className="text-right">
                  <div className="flex justify-end">
                    <OverflowMenu
                      label={`Actions for ${row.name}`}
                      data-testid={`tps-directory-actions-${row.tpsId}`}
                      items={[
                        {
                          label: "Fee schedule…",
                          onSelect: () => onOpenFees(row),
                        },
                        {
                          label: "Loans & switching…",
                          onSelect: () => onOpenLoans(row),
                        },
                      ]}
                    />
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}

// ——— Approval monitoring ———

function ApprovalSection({
  query,
}: {
  query: ReturnType<typeof useTpsApprovalQueue>;
}) {
  const { tz } = useDateFormatter();
  return (
    <section>
      <SectionTitle>Approval monitoring</SectionTitle>
      {query.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : query.isError ? (
        <p className="text-small text-status-error" role="alert">
          Couldn't load the approval queue. Try again.
        </p>
      ) : (
        <div className="flex flex-col gap-space-4">
          <div>
            <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
              Pending bills (TPS approval)
            </h3>
            {!query.data || query.data.pendingBills.length === 0 ? (
              <EmptyState title="No pending bills" />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Loan</TH>
                    <TH>TPS</TH>
                    <TH>Period</TH>
                    <TH className="text-right">Amount due</TH>
                    <TH className="text-right">Age (days)</TH>
                  </TR>
                </THead>
                <TBody>
                  {query.data.pendingBills.map((b) => (
                    <TR
                      key={b.statementId}
                      data-testid={`tps-pending-bill-${b.statementId}`}
                    >
                      <TD className="font-semibold text-onyx">{b.loanNumber}</TD>
                      <TD className="text-gray-mid">{b.tpsName ?? "—"}</TD>
                      <TD className="text-gray-mid">
                        {formatCalendarDate(b.periodStart)} –{" "}
                        {formatCalendarDate(b.periodEnd)}
                      </TD>
                      <TD className="text-right tabular-nums">
                        {formatCurrency(b.amountDue, {
                          maximumFractionDigits: 2,
                        })}
                      </TD>
                      <TD className="text-right tabular-nums">{b.ageDays}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </div>

          <div>
            <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
              Pending distributions
            </h3>
            {!query.data || query.data.pendingDistributions.length === 0 ? (
              <EmptyState title="No pending distributions" />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Loan</TH>
                    <TH>TPS</TH>
                    <TH>Period</TH>
                    <TH className="text-right">Total</TH>
                    <TH>Funds cleared</TH>
                    <TH>Block reason</TH>
                    <TH className="text-right">Age (days)</TH>
                  </TR>
                </THead>
                <TBody>
                  {query.data.pendingDistributions.map((d) => (
                    <TR
                      key={d.distributionId}
                      data-testid={`tps-pending-dist-${d.distributionId}`}
                    >
                      <TD className="font-semibold text-onyx">{d.loanNumber}</TD>
                      <TD className="text-gray-mid">{d.tpsName ?? "—"}</TD>
                      <TD className="text-gray-mid">
                        {formatCalendarDate(d.periodStart)}
                      </TD>
                      <TD className="text-right tabular-nums">
                        {formatCurrency(d.totalAmount, {
                          maximumFractionDigits: 2,
                        })}
                      </TD>
                      <TD className="text-gray-mid">
                        {d.fundsClearedAt
                          ? formatDateInTz(d.fundsClearedAt, tz)
                          : "Not cleared"}
                      </TD>
                      <TD className="text-gray-mid">
                        {d.blockReason ? (
                          <span className="text-status-warning">
                            {d.blockReason}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TD>
                      <TD className="text-right tabular-nums">{d.ageDays}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ——— Correction queue ———

function CorrectionSection({
  query,
}: {
  query: ReturnType<typeof useTpsCorrectionQueue>;
}) {
  return (
    <section>
      <SectionTitle>Correction queue</SectionTitle>
      {query.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : query.isError ? (
        <p className="text-small text-status-error" role="alert">
          Couldn't load the correction queue. Try again.
        </p>
      ) : (
        <div className="flex flex-col gap-space-4">
          <div>
            <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
              Returned bills
            </h3>
            {!query.data || query.data.returnedBills.length === 0 ? (
              <EmptyState title="No returned bills" />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Loan</TH>
                    <TH>TPS</TH>
                    <TH>Period</TH>
                    <TH className="text-right">Amount due</TH>
                    <TH>Returned reason</TH>
                  </TR>
                </THead>
                <TBody>
                  {query.data.returnedBills.map((b) => (
                    <TR key={b.id} data-testid={`tps-returned-bill-${b.id}`}>
                      <TD className="font-semibold text-onyx">
                        <Link
                          to={`/portfolio-admin/loans/${b.loanId}?tab=financials`}
                          className="underline-offset-2 hover:underline"
                        >
                          {b.loanNumber}
                        </Link>
                      </TD>
                      <TD className="text-gray-mid">{b.tpsName ?? "—"}</TD>
                      <TD className="text-gray-mid">
                        {formatCalendarDate(b.periodStart)} –{" "}
                        {formatCalendarDate(b.periodEnd)}
                      </TD>
                      <TD className="text-right tabular-nums">
                        {formatCurrency(b.amountDue, {
                          maximumFractionDigits: 2,
                        })}
                      </TD>
                      <TD className="text-gray-mid">
                        {b.returnedReason ?? "—"}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </div>

          <div>
            <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
              Attention distributions
            </h3>
            {!query.data ||
            query.data.attentionDistributions.length === 0 ? (
              <EmptyState title="No distributions need attention" />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Loan</TH>
                    <TH>TPS</TH>
                    <TH>Status</TH>
                    <TH className="text-right">Total</TH>
                    <TH>Block / reversal reason</TH>
                  </TR>
                </THead>
                <TBody>
                  {query.data.attentionDistributions.map((d) => (
                    <TR
                      key={d.distributionId}
                      data-testid={`tps-attention-dist-${d.distributionId}`}
                    >
                      <TD className="font-semibold text-onyx">{d.loanNumber}</TD>
                      <TD className="text-gray-mid">{d.tpsName ?? "—"}</TD>
                      <TD>
                        <Badge status="error">{d.status}</Badge>
                      </TD>
                      <TD className="text-right tabular-nums">
                        {formatCurrency(d.totalAmount, {
                          maximumFractionDigits: 2,
                        })}
                      </TD>
                      <TD className="text-gray-mid">
                        {d.reversalReason ?? d.blockReason ?? "—"}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ——— Money oversight ———

function MoneySection({
  query,
}: {
  query: ReturnType<typeof useTpsMoneyOversight>;
}) {
  return (
    <section>
      <div className="mb-space-3 flex items-center justify-between gap-space-3">
        <h2 className="font-display text-h3 font-bold text-onyx m-0">
          Money oversight
        </h2>
        <Link
          to="/portfolio-admin/income-overview"
          className="text-small text-gold-shadow underline-offset-4 hover:underline"
        >
          View full income overview
        </Link>
      </div>
      {query.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : query.isError || !query.data ? (
        <p className="text-small text-status-error" role="alert">
          Couldn't load the money summary.
        </p>
      ) : (
        <div
          className="grid grid-cols-2 gap-space-3 md:grid-cols-3 lg:grid-cols-6"
          data-testid="tps-oversight-money"
        >
          <Kpi
            label="Cleared in"
            value={formatCurrency(query.data.clearingInTotal)}
          />
          <Kpi
            label="Distributed out"
            value={formatCurrency(query.data.distributedOutTotal)}
          />
          <Kpi
            label="Pending dist"
            value={formatCurrency(query.data.pendingDistributionTotal)}
          />
          <Kpi
            label="Reversed"
            value={formatCurrency(query.data.reversedTotal)}
          />
          <Kpi label="Transfers created" value={query.data.transfersCreated} />
          <Kpi
            label="Transfers reversed"
            value={query.data.transfersReversed}
          />
        </div>
      )}
    </section>
  );
}

// ——— Shared ———

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-display text-h3 font-bold text-onyx m-0 mb-space-3">
      {children}
    </h2>
  );
}
